import { useEffect, useState } from "react";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n-dict";
import { initConsent, setConsent, type ConsentChoice, type ConsentState } from "@/lib/consent";
import { LocaleLink } from "./LocaleLink";

// Regional consent banner: rendered only for visitors in regions that
// require consent (or when the region cannot be determined). Reopening via
// the footer's "Cookie settings" keeps the current choice until the visitor
// actively changes it.
export function CookieConsent() {
  const t = useT();
  const [state, setState] = useState<ConsentState | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    void initConsent().then((s) => {
      if (!alive) return;
      setState(s);
      setOpen(s.requiresConsent && !s.choice);
    });
    const reopen = () => setOpen(true);
    window.addEventListener("fo:cookie-settings", reopen);
    return () => {
      alive = false;
      window.removeEventListener("fo:cookie-settings", reopen);
    };
  }, []);

  if (!state?.requiresConsent || !open) return null;

  const choose = (choice: ConsentChoice) => {
    setConsent(choice);
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-label={t("Cookies and ad measurement")}
      className="fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6"
    >
      <div className="relative mx-auto max-w-2xl rounded-2xl border border-border bg-card p-5 shadow-2xl">
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label={t("Close")}
          className="absolute top-3 right-3 text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="size-4" />
        </button>
        <h2 className="font-display text-base font-semibold">
          {t("Cookies and ad measurement")}
        </h2>
        <p className="mt-2 pr-6 text-sm leading-relaxed text-muted-foreground">
          {t(
            "We use cookies to measure how our advertising performs and to improve the site. You can accept or reject — and change your choice anytime in Cookie settings.",
          )}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => choose("granted")}>
            {t("Accept")}
          </Button>
          <Button size="sm" variant="outline" onClick={() => choose("denied")}>
            {t("Reject")}
          </Button>
          <LocaleLink
            to="/privacy"
            className="ml-auto text-sm text-primary hover:underline"
          >
            {t("Learn more")}
          </LocaleLink>
        </div>
      </div>
    </div>
  );
}
